import ReactQuill from "react-quill-new";
import { useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash";
import { Col, Row } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import ErrorMessage from "../../../components/ErrorMessage";
import Badge from "../../../components/Badge";
import { editSettingValidation } from "../../../utils/validation/validation";
import { settingAdminContext } from "../../../context/AdminContext/SettingAdminContext/SettingAdminContext";
import { numberSeparate, removeNumberSeparate } from "../../../utils";
import {
  convertPaymentGatewayToLogo,
  convertPaymentGatewayToText,
  paymentGatewayItems,
} from "../../../utils/enum";

// Define Quill modules and formats outside the component for reuse
const quillModules = {
  toolbar: [
    [{ font: [] }, { size: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ header: 1 }, { header: 2 }, "blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ direction: "rtl" }, { align: [] }],
    ["link", "image", "video", "formula"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const quillFormats = [
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "header",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "direction",
  "align",
  "link",
  "image",
  "video",
  "formula",
];

const SettingAdmin = () => {
  const { setting, handleGetSetting, handleEditSetting } =
    useContext(settingAdminContext);

  // Validation
  const {
    watch,
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editSettingValidation),
  });

  const handleSetSetting = () => {
    reset({
      isRegisterStaff: setting?.isRegisterStaff,
      invoiceExpiration: setting?.invoiceExpiration,
      terms: setting?.terms,
      staffTerms: setting?.staffTerms,
      aboutUs: setting?.aboutUs,
      taxPercent: setting?.taxPercent,
      paymentGateway: setting?.paymentGateway,
      notifications: setting?.notifications,
    });
  };

  useEffect(() => {
    if (!isEmpty(setting)) {
      handleSetSetting();
    }
  }, [setting]);

  useEffect(() => {
    handleGetSetting();
  }, []);

  // Rich text editor component to ensure consistent implementation
  const RichTextEditor = ({ name, placeholder, control }) => (
    <div className="quill-editor-container">
      <Controller
        control={control}
        render={({ field: { value, onChange, onBlur } }) => (
          <ReactQuill
            className="border radius-8 my-3"
            theme="snow"
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder={placeholder}
          />
        )}
        name={name}
      />
    </div>
  );

  const notification = [
    { key: "push", label: "اعلان داخلی" },
    { key: "email", label: "ارسال پست الکترونیک" },
    { key: "sms", label: "ارسال پیامک" },
  ];
  const notifications = [
    { key: "register", label: "عضویت در سایت", items: notification },
    { key: "order", label: "ثبت سفارش", items: notification },
    { key: "payment", label: "پرداخت وجه", items: notification },
    {
      key: "changeOrderStatus",
      label: "تغییر وضعیت سفارش",
      items: notification,
    },
    { key: "newTicket", label: "ثبت تیکت جدید", items: notification },
    { key: "replyTicket", label: "جواب به تیکت", items: notification },
    {
      key: "changeTicketStatus",
      label: "تغییر وضعیت تیکت",
      items: notification,
    },
    {
      key: "bulkSMS",
      label: "پیامک انبوه",
      items: notification,
    },
  ];

  return (
    <>
      <SiteTitle title="تنظیمات" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form autoComplete="off" onSubmit={handleSubmit(handleEditSetting)}>
            <div className="row g-2">
              <div className="col-12 col-md-4">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="taxPercent"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      مالیات <Badge color="primary">درصد</Badge>
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          type="text"
                          placeholder="مالیات را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="taxPercent"
                    />
                  </div>

                  <ErrorMessage message={errors.taxPercent?.message} />
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="invoiceExpiration"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      مدت انقضا فاکتور <Badge color="primary">روز</Badge>
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          type="text"
                          placeholder="مدت انقضا فاکتور را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="invoiceExpiration"
                    />
                  </div>

                  <ErrorMessage message={errors.invoiceExpiration?.message} />
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="mb-20">
                  <label
                    htmlFor="isRegisterStaff"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    ثبت نام همکاران
                  </label>
                  <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative">
                    <label
                      htmlFor="isRegisterStaff"
                      className="position-absolute w-100 h-100 start-0 top-0"
                    />
                    <div className="d-flex align-items-center gap-3 justify-content-between">
                      <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                        فعال
                      </span>
                      <Controller
                        control={control}
                        render={({ field: { value, onChange, onBlur } }) => (
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="isRegisterStaff"
                            onChange={(e) => {
                              const value = e.target.checked;
                              onChange(value);
                            }}
                            onBlur={onBlur}
                            checked={value}
                          />
                        )}
                        name="isRegisterStaff"
                      />
                    </div>
                  </div>
                  <ErrorMessage message={errors.isRegisterStaff?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="card radius-12 shadow-none border overflow-hidden">
                  <div className="card-header bg-neutral-100 border-bottom py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center gap-10">
                      <span className="w-36-px h-36-px bg-base rounded-circle d-flex justify-content-center align-items-center">
                        <Icon
                          icon="solar:bell-broken"
                          className="text-lg fw-semibold text-primary-light"
                        />
                      </span>
                      <span className="text-lg fw-semibold text-primary-light">
                        اعلانات
                      </span>
                    </div>

                    <ErrorMessage message={errors.notifications?.message} />
                  </div>
                  <div className="card-body p-24">
                    {notifications?.map((item) => (
                      <div className="mb-20" key={item.key}>
                        <label
                          htmlFor={item.key}
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          {item.label}
                        </label>
                        <Row>
                          {item.items?.map((_item) => {
                            const _id = `notifications.${item.key}.${_item.key}`;
                            return (
                              <Col xs={12} md={4} key={_id}>
                                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative">
                                  <label
                                    htmlFor={_id}
                                    className="position-absolute w-100 h-100 start-0 top-0"
                                  />
                                  <div className="d-flex align-items-center gap-3 justify-content-between">
                                    <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                                      {_item.label}
                                    </span>
                                    <Controller
                                      control={control}
                                      render={({
                                        field: { value, onChange, onBlur },
                                      }) => (
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          role="switch"
                                          id={_id}
                                          onChange={(e) => {
                                            const value = e.target.checked;
                                            onChange(value);
                                          }}
                                          onBlur={onBlur}
                                          checked={value}
                                        />
                                      )}
                                      name={_id}
                                    />
                                  </div>
                                </div>
                                <ErrorMessage
                                  message={
                                    errors.notifications &&
                                    errors.notifications[item.key] &&
                                    errors.notifications[item.key][_item.key]
                                      ?.message
                                  }
                                />
                              </Col>
                            );
                          })}
                        </Row>

                        <ErrorMessage
                          message={
                            errors.notifications &&
                            errors.notifications[item.key]?.message
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="card radius-12 shadow-none border overflow-hidden">
                  <div className="card-header bg-neutral-100 border-bottom py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center gap-10">
                      <span className="w-36-px h-36-px bg-base rounded-circle d-flex justify-content-center align-items-center">
                        <img
                          src={convertPaymentGatewayToLogo(
                            paymentGatewayItems[0]
                          )}
                          alt=""
                          className="img-fluid rounded-circle"
                        />
                      </span>
                      <span className="text-lg fw-semibold text-primary-light">
                        {convertPaymentGatewayToText(paymentGatewayItems[0])}
                      </span>
                    </div>

                    <div className="form-switch switch-primary d-flex align-items-center justify-content-center">
                      <Controller
                        control={control}
                        render={({ field: { value, onChange, onBlur } }) => (
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="paymentGateway.zarinpal.isActive"
                            onChange={(e) => {
                              const value = e.target.checked;
                              onChange(value);
                            }}
                            onBlur={onBlur}
                            checked={value}
                          />
                        )}
                        name="paymentGateway.zarinpal.isActive"
                      />
                    </div>
                    <ErrorMessage
                      message={
                        errors.paymentGateway?.zarinpal?.isActive?.message
                      }
                    />
                  </div>
                  <div className="card-body p-24">
                    <div className="row">
                      <div className="col-12">
                        <div className="mb-20">
                          <div>
                            <label
                              htmlFor="paymentGateway.zarinpal.merchantId"
                              className="form-label fw-semibold text-primary-light text-sm mb-8"
                            >
                              کد درگاه پرداخت
                            </label>
                            <input
                              disabled={
                                !watch("paymentGateway.zarinpal.isActive")
                              }
                              type="text"
                              className="form-control radius-8"
                              id="paymentGateway.zarinpal.merchantId"
                              placeholder="کد درگاه پرداخت‌ را وارد نمایید"
                              {...register(
                                "paymentGateway.zarinpal.merchantId"
                              )}
                            />
                          </div>

                          <ErrorMessage
                            message={
                              errors.paymentGateway?.zarinpal?.merchantId
                                ?.message
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <div>
                            <label
                              htmlFor="paymentGateway.zarinpal.baseUrl"
                              className="form-label fw-semibold text-primary-light text-sm mb-8"
                            >
                              آدرس وب سرویس
                            </label>
                            <input
                              disabled={
                                !watch("paymentGateway.zarinpal.isActive")
                              }
                              type="text"
                              className="form-control radius-8"
                              id="paymentGateway.zarinpal.baseUrl"
                              placeholder="آدرس وب سرویس‌ را وارد نمایید"
                              {...register("paymentGateway.zarinpal.baseUrl")}
                            />
                          </div>

                          <ErrorMessage
                            message={
                              errors.paymentGateway?.zarinpal?.baseUrl?.message
                            }
                          />
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-20">
                          <div>
                            <label
                              htmlFor="paymentGateway.zarinpal.callbackUrl"
                              className="form-label fw-semibold text-primary-light text-sm mb-8"
                            >
                              ‌ آدرس بازگشت مشتری
                            </label>
                            <input
                              disabled={
                                !watch("paymentGateway.zarinpal.isActive")
                              }
                              type="text"
                              className="form-control radius-8"
                              id="paymentGateway.zarinpal.callbackUrl"
                              placeholder="آدرس بازگشت مشتری را وارد نمایید"
                              {...register(
                                "paymentGateway.zarinpal.callbackUrl"
                              )}
                            />
                          </div>

                          <ErrorMessage
                            message={
                              errors.paymentGateway?.zarinpal?.callbackUrl
                                ?.message
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="mb-20">
                          <div>
                            <label
                              htmlFor="paymentGateway.zarinpal.description"
                              className="form-label fw-semibold text-primary-light text-sm mb-8"
                            >
                              توضیحات
                            </label>
                            <textarea
                              disabled={
                                !watch("paymentGateway.zarinpal.isActive")
                              }
                              name="paymentGateway.zarinpal.description"
                              className="form-control radius-8"
                              id="paymentGateway.zarinpal.description"
                              rows={5}
                              placeholder="توضیحات ..."
                              {...register(
                                "paymentGateway.zarinpal.description"
                              )}
                            />
                          </div>
                          <ErrorMessage
                            message={
                              errors.paymentGateway?.zarinpal?.description
                                ?.message
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <label
                    htmlFor="terms"
                    className="form-label fw-semibold text-primary-light text-sm"
                  >
                    قوانین و مقررات{" "}
                    <span className="badge text-primary-600 bg-primary-100 radius-4">
                      عمومی
                    </span>
                  </label>
                  <RichTextEditor
                    name="terms"
                    placeholder="قوانین و مقررات عمومی را بنویسید..."
                    control={control}
                  />
                  <ErrorMessage message={errors.terms?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <label
                    htmlFor="staffTerms"
                    className="form-label fw-semibold text-primary-light text-sm"
                  >
                    قوانین و مقررات{" "}
                    <span className="badge text-primary-600 bg-primary-100 radius-4">
                      همکار
                    </span>
                  </label>
                  <RichTextEditor
                    name="staffTerms"
                    placeholder="قوانین و مقررات همکار را بنویسید..."
                    control={control}
                  />
                  <ErrorMessage message={errors.staffTerms?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <label
                    htmlFor="aboutUs"
                    className="form-label fw-semibold text-primary-light text-sm"
                  >
                    درباره ما
                  </label>
                  <RichTextEditor
                    name="aboutUs"
                    placeholder="درباره ما را بنویسید..."
                    control={control}
                  />
                  <ErrorMessage message={errors.aboutUs?.message} />
                </div>
              </div>
            </div>
            <Row className="align-items-center justify-content-between">
              <Col>
                <button
                  type="button"
                  onClick={handleSetSetting}
                  className="border border-danger-600 bg-danger-50 bg-hover-danger-100 text-danger-600 text-md w-100 py-11 radius-8 text-center"
                >
                  بازنشانی
                </button>
              </Col>
              <Col>
                <button
                  type="submit"
                  className="btn btn-primary border border-primary-600 text-md w-100 py-12 radius-8"
                >
                  ذخیره
                </button>
              </Col>
            </Row>
          </form>
        </div>
      </div>
    </>
  );
};

export default SettingAdmin;
